import React, { useState } from 'react';
import './Login.css';
import { apiFetch, ApiError } from './api';
import { salvarAuth } from './auth';

// Mensagens pt-BR por código de erro do backend (contrato SEF-66/68).
const MENSAGENS_ERRO = {
  INVALID_CREDENTIALS: 'E-mail ou senha incorretos.',
  ACCOUNT_DISABLED:
    'Sua conta está desativada. Fale com o administrador do escritório.',
  EMAIL_NOT_VERIFIED:
    'Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.',
  VALIDATION_ERROR: 'Preencha e-mail e senha corretamente.',
  RATE_LIMITED:
    'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.',
};

function mensagemErro(erro) {
  if (erro instanceof ApiError) {
    if (erro.status === 429) return MENSAGENS_ERRO.RATE_LIMITED;
    if (erro.code && MENSAGENS_ERRO[erro.code]) {
      return MENSAGENS_ERRO[erro.code];
    }
    return 'Não foi possível entrar. Tente novamente em instantes.';
  }
  return 'Erro de conexão. Verifique sua internet e tente novamente.';
}

function Login({ onLogin, sessaoExpirada = false }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [senhaVisivel, setSenhaVisivel] = useState(false);

  // Estado do modal "Esqueceu a senha?"
  const [mostrarRecuperar, setMostrarRecuperar] = useState(false);
  const [emailRecuperar, setEmailRecuperar] = useState('');
  const [enviandoRecuperar, setEnviandoRecuperar] = useState(false);
  const [recuperarEnviado, setRecuperarEnviado] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);
    setErro('');

    try {
      const dados = await apiFetch('/api/auth/login', {
        method: 'POST',
        auth: false,
        body: { email, senha, lembrar },
      });

      const usuario = {
        id: dados.id,
        nome: dados.nome,
        email: dados.email,
        cargo: dados.cargo,
        avatar: dados.avatar ?? null,
      };

      salvarAuth({
        accessToken: dados.accessToken,
        refreshToken: dados.refreshToken,
        usuario,
        lembrar,
      });

      onLogin(usuario);
    } catch (error) {
      setErro(mensagemErro(error));
    } finally {
      setCarregando(false);
    }
  };

  const handleRecuperar = async (e) => {
    e.preventDefault();
    setEnviandoRecuperar(true);
    try {
      // Backend sempre responde 202 (tempo constante, não vaza existência).
      await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        auth: false,
        body: { email: emailRecuperar },
      });
    } catch {
      // Mesmo em erro de rede mantemos a mensagem neutra para não vazar
      // existência de conta.
    } finally {
      setEnviandoRecuperar(false);
      setRecuperarEnviado(true);
    }
  };

  const abrirRecuperar = () => {
    setEmailRecuperar(email);
    setRecuperarEnviado(false);
    setMostrarRecuperar(true);
  };

  const fecharRecuperar = () => {
    setMostrarRecuperar(false);
    setEnviandoRecuperar(false);
  };

  return (
    <div className="login-page">
      {/* Lado esquerdo - Branding */}
      <div className="login-branding">
        <div className="login-branding-content">
          <img src="/logo.png" alt="Sefira" className="login-brand-logo" />
          <h1 className="login-brand-title">
            Gestão inteligente de<br />propriedade intelectual
          </h1>
          <p className="login-brand-subtitle">
            Plataforma completa para escritórios de advocacia especializados em marcas e patentes.
          </p>
          <div className="login-brand-features">
            <div className="login-brand-feature">
              <div className="login-brand-feature-icon">
                <i className="fa-solid fa-shield-check"></i>
              </div>
              <div>
                <strong>Monitoramento INPI</strong>
                <span>Acompanhamento automático de publicações</span>
              </div>
            </div>
            <div className="login-brand-feature">
              <div className="login-brand-feature-icon">
                <i className="fa-solid fa-chart-mixed"></i>
              </div>
              <div>
                <strong>Controle Financeiro</strong>
                <span>Gestão completa de receitas e despesas</span>
              </div>
            </div>
            <div className="login-brand-feature">
              <div className="login-brand-feature-icon">
                <i className="fa-solid fa-users-gear"></i>
              </div>
              <div>
                <strong>CRM Integrado</strong>
                <span>Gestão de clientes e leads em um só lugar</span>
              </div>
            </div>
          </div>
        </div>
        <div className="login-brand-pattern"></div>
      </div>

      {/* Lado direito - Formulario */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>Bem-vindo de volta</h2>
            <p>Entre com suas credenciais para acessar a plataforma</p>
          </div>

          {sessaoExpirada && !erro && (
            <div className="login-aviso" role="alert">
              <i className="fa-solid fa-clock-rotate-left"></i>
              Sua sessão expirou. Faça login novamente.
            </div>
          )}

          {erro && (
            <div className="login-error" role="alert">
              <i className="fa-solid fa-circle-exclamation"></i>
              {erro}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="email">E-mail</label>
              <div className="login-input-wrapper">
                <i className="fa-solid fa-envelope login-input-icon"></i>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="senha">Senha</label>
              <div className="login-input-wrapper">
                <i className="fa-solid fa-lock login-input-icon"></i>
                <input
                  type={senhaVisivel ? 'text' : 'password'}
                  id="senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite sua senha"
                  required
                />
                <button
                  type="button"
                  className="login-toggle-password"
                  onClick={() => setSenhaVisivel(!senhaVisivel)}
                  tabIndex={-1}
                >
                  <i className={`fa-solid ${senhaVisivel ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="login-options">
              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={lembrar}
                  onChange={(e) => setLembrar(e.target.checked)}
                />
                <span className="login-checkbox-mark"></span>
                Lembrar-me
              </label>
              <button type="button" className="login-forgot" onClick={abrirRecuperar}>
                Esqueceu a senha?
              </button>
            </div>

            <button
              type="submit"
              className={`login-submit ${carregando ? 'loading' : ''}`}
              disabled={carregando}
            >
              {carregando ? (
                <>
                  <div className="login-spinner"></div>
                  Entrando...
                </>
              ) : (
                <>
                  Entrar
                  <i className="fa-solid fa-arrow-right"></i>
                </>
              )}
            </button>
          </form>

          <div className="login-footer-text">
            Não tem uma conta?{' '}
            <button type="button" className="login-signup-link" onClick={() => alert('Funcionalidade em desenvolvimento')}>
              Cadastre-se
            </button>
          </div>
        </div>
      </div>

      {mostrarRecuperar && (
        <div className="login-modal-overlay" onClick={fecharRecuperar}>
          <div
            className="login-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Recuperar senha"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="login-modal-close"
              onClick={fecharRecuperar}
              aria-label="Fechar"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            {recuperarEnviado ? (
              <div className="login-modal-sucesso">
                <i className="fa-solid fa-envelope-circle-check"></i>
                <h3>Verifique seu e-mail</h3>
                <p>
                  Se houver uma conta associada a <strong>{emailRecuperar}</strong>,
                  enviamos um link para redefinir sua senha. O link expira em 30 minutos.
                </p>
                <button
                  type="button"
                  className="login-submit"
                  onClick={fecharRecuperar}
                >
                  Entendi
                </button>
              </div>
            ) : (
              <form onSubmit={handleRecuperar}>
                <h3>Recuperar senha</h3>
                <p className="login-modal-texto">
                  Informe o e-mail cadastrado. Enviaremos um link para você
                  redefinir sua senha.
                </p>
                <div className="login-field">
                  <label htmlFor="email-recuperar">E-mail</label>
                  <div className="login-input-wrapper">
                    <i className="fa-solid fa-envelope login-input-icon"></i>
                    <input
                      type="email"
                      id="email-recuperar"
                      value={emailRecuperar}
                      onChange={(e) => setEmailRecuperar(e.target.value)}
                      placeholder="seu@email.com"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className={`login-submit ${enviandoRecuperar ? 'loading' : ''}`}
                  disabled={enviandoRecuperar}
                >
                  {enviandoRecuperar ? (
                    <>
                      <div className="login-spinner"></div>
                      Enviando...
                    </>
                  ) : (
                    'Enviar link de recuperação'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
